import React from 'react';
import { 
  Phone, Mail, MapPin, Clock, Star, ChevronRight, ChevronDown,
  MessageCircle, Calendar, Menu, X, Check, ArrowRight, ArrowUpRight,
  Zap, Wrench, Lightbulb, LayoutGrid, FileCheck, Home as HomeIcon,
  Play, Quote
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// Icon mapping for services
const iconMap = {
  zap: Zap,
  wrench: Wrench,
  lightbulb: Lightbulb,
  'layout-grid': LayoutGrid,
  'file-check': FileCheck,
  home: HomeIcon
};

const TemplateBarcelona = ({ business, isPreview = false, isMobile = false }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [showAllServices, setShowAllServices] = React.useState(false);
  const [showAllGallery, setShowAllGallery] = React.useState(false);
  const [activeTestimonial, setActiveTestimonial] = React.useState(0);

  const {
    name = '',
    category = '',
    city = '',
    province = '',
    phone = '',
    whatsapp = '',
    email = '',
    logo = '',
    headline = '',
    description = '',
    services = [],
    gallery = [],
    testimonials = [],
    service_areas = [],
    business_hours = null,
    work_process = [],
    design_settings = {}
  } = business || {};

  const primaryColor = design_settings.primary_color || '#dc2626';
  const secondaryColor = design_settings.secondary_color || '#f97316';
  const heroImage = design_settings.hero_image || 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1200';

  const visibleServices = showAllServices ? services : services.slice(0, 4);
  const visibleGallery = showAllGallery ? gallery : gallery.slice(0, 3);

  // Auto-rotate testimonials - sempre no topo, antes de qualquer return condicional
  React.useEffect(() => {
    if (testimonials.length > 1) {
      const interval = setInterval(() => {
        setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length]);

  if (!business) return null;

  const handleWhatsApp = (message = '') => {
    const defaultMsg = message || `Hola, he visto tu página y me gustaría solicitar información sobre tus servicios.`;
    const phoneNum = (whatsapp || phone)?.replace(/\D/g, '');
    window.open(`https://wa.me/${phoneNum}?text=${encodeURIComponent(defaultMsg)}`, '_blank');
  };

  const handlePresupuesto = () => {
    document.getElementById('cta-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCita = () => {
    handleWhatsApp('Hola, me gustaría agendar una cita para un servicio.');
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      electricista: 'Electricista',
      fontanero: 'Fontanero',
      reformas: 'Reformas',
      limpieza: 'Limpieza',
      jardineria: 'Jardinería'
    };
    return labels[cat] || cat;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-['Outfit',sans-serif]">
      {/* ========== HEADER - Floating transparent ========== */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl px-4 py-3 border border-white/10">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center gap-3">
                {logo ? (
                  <img src={logo} alt={name} className="h-8 w-auto" />
                ) : (
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center font-bold"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {name?.charAt(0)}
                    </div>
                    <span className="font-bold text-lg hidden sm:block">{name}</span>
                  </div>
                )}
              </div>

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-8 text-sm">
                <a href="#servicios" className="text-white/70 hover:text-white transition-colors">Servicios</a>
                <a href="#trabajos" className="text-white/70 hover:text-white transition-colors">Portfolio</a>
                <a href="#proceso" className="text-white/70 hover:text-white transition-colors">Proceso</a>
                <a href="#opiniones" className="text-white/70 hover:text-white transition-colors">Opiniones</a>
              </nav>

              {/* CTA Desktop */}
              <div className="hidden md:flex items-center gap-3">
                <button 
                  onClick={() => handleWhatsApp()}
                  className="p-2 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
                >
                  <MessageCircle size={20} />
                </button>
                <Button 
                  onClick={handlePresupuesto}
                  className="rounded-full px-6"
                  style={{ backgroundColor: primaryColor }}
                >
                  Contactar
                  <ArrowUpRight size={16} className="ml-2" />
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <button 
                className="lg:hidden p-2 rounded-lg bg-white/10"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden mx-4 mt-2 bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <nav className="flex flex-col gap-4">
              <a href="#servicios" className="text-lg text-white/80 hover:text-white" onClick={() => setMenuOpen(false)}>Servicios</a>
              <a href="#trabajos" className="text-lg text-white/80 hover:text-white" onClick={() => setMenuOpen(false)}>Portfolio</a>
              <a href="#proceso" className="text-lg text-white/80 hover:text-white" onClick={() => setMenuOpen(false)}>Proceso</a>
              <a href="#opiniones" className="text-lg text-white/80 hover:text-white" onClick={() => setMenuOpen(false)}>Opiniones</a>
            </nav>
          </div>
        )}
      </header>

      {/* ========== HERO - Asymmetric Layout ========== */}
      <section className="min-h-screen relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0">
          <div 
            className="absolute top-0 right-0 w-[70%] h-[80%] rounded-bl-[100px] bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-slate-950/50 to-slate-950" />
          </div>
          {/* Decorative elements */}
          <div 
            className="absolute bottom-20 left-20 w-72 h-72 rounded-full blur-[120px] opacity-30"
            style={{ backgroundColor: primaryColor }}
          />
          <div 
            className="absolute top-40 right-40 w-96 h-96 rounded-full blur-[150px] opacity-20"
            style={{ backgroundColor: secondaryColor }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 pt-32 pb-20 min-h-screen flex flex-col justify-center">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1">
              {/* Category tag */}
              <div className="inline-flex items-center gap-2 mb-6">
                <span 
                  className="w-3 h-3 rounded-full animate-pulse"
                  style={{ backgroundColor: primaryColor }}
                />
                <span className="text-white/60 text-sm uppercase tracking-wider">
                  {getCategoryLabel(category)} • {city}
                </span>
              </div>

              {/* Main headline - Large typography */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-[1.1]">
                <span className="text-white">{headline?.split(' ').slice(0, 2).join(' ')}</span>
                <br />
                <span style={{ color: primaryColor }}>
                  {headline?.split(' ').slice(2).join(' ')}
                </span>
              </h1>

              {/* Description */}
              {description && (
                <p className="text-lg text-white/60 mb-8 max-w-md leading-relaxed">
                  {description}
                </p>
              )}

              {/* Stats row */}
              <div className="flex items-center gap-8 mb-10">
                <div>
                  <div className="text-3xl font-bold" style={{ color: primaryColor }}>15+</div>
                  <div className="text-sm text-white/50">Años experiencia</div>
                </div>
                <div className="w-px h-12 bg-white/20" />
                <div>
                  <div className="text-3xl font-bold">500+</div>
                  <div className="text-sm text-white/50">Proyectos</div>
                </div>
                <div className="w-px h-12 bg-white/20" />
                <div className="flex items-center gap-1">
                  <Star size={20} className="text-yellow-400 fill-yellow-400" />
                  <span className="text-3xl font-bold">5.0</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={handlePresupuesto}
                  className="rounded-full px-8 py-6 text-base font-semibold group"
                  style={{ backgroundColor: primaryColor }}
                >
                  Solicitar presupuesto
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => handleWhatsApp()}
                  className="rounded-full px-8 py-6 text-base border-white/20 text-white hover:bg-white/10"
                >
                  <MessageCircle size={18} className="mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>

            {/* Right - Hero image on mobile */}
            <div className="order-1 lg:order-2 lg:hidden">
              <div 
                className="aspect-[4/3] rounded-3xl bg-cover bg-center"
                style={{ backgroundImage: `url(${heroImage})` }}
              />
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-white/40">
            <span className="text-xs uppercase tracking-wider">Scroll</span>
            <ChevronDown size={20} className="animate-bounce" />
          </div>
        </div>
      </section>

      {/* ========== SERVICIOS - Bento Grid ========== */}
      <section id="servicios" className="py-20 sm:py-28 relative">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6">
            <div>
              <span className="text-sm uppercase tracking-wider text-white/40 mb-2 block">Servicios</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                ¿Qué <span style={{ color: primaryColor }}>necesitas</span>?
              </h2>
            </div>
            <p className="text-white/60 max-w-md">
              Servicios profesionales con materiales de primera calidad y garantía de satisfacción.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {visibleServices.map((service, idx) => {
              const IconComponent = iconMap[service.icon] || Zap;
              // Make first item larger
              const isLarge = idx === 0;
              
              return (
                <div 
                  key={service.id}
                  className={`
                    group relative rounded-3xl overflow-hidden cursor-pointer
                    ${isLarge ? 'col-span-2 row-span-2' : ''}
                  `}
                  style={{ 
                    backgroundColor: idx === 0 ? primaryColor : 'rgba(255,255,255,0.05)',
                    minHeight: isLarge ? '320px' : '160px'
                  }}
                >
                  <div className="absolute inset-0 p-6 flex flex-col justify-between">
                    <div 
                      className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                        isLarge ? 'bg-white/20' : 'bg-white/10'
                      } group-hover:scale-110 transition-transform`}
                    >
                      <IconComponent size={isLarge ? 28 : 24} className="text-white" />
                    </div>
                    <div>
                      <h3 className={`font-bold text-white mb-1 ${isLarge ? 'text-2xl' : 'text-base'}`}>
                        {service.name}
                      </h3>
                      {isLarge && service.description && (
                        <p className="text-white/70 text-sm">{service.description}</p>
                      )}
                      {service.price && (
                        <span className="text-sm text-white/60 mt-2 block">{service.price}</span>
                      )}
                    </div>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              );
            })}
          </div>

          {services.length > 4 && !showAllServices && (
            <div className="text-center mt-10">
              <Button 
                variant="outline"
                onClick={() => setShowAllServices(true)}
                className="rounded-full border-white/20 text-white hover:bg-white/10"
              >
                Ver todos los servicios
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ========== PORTFOLIO / TRABAJOS ========== */}
      {gallery.length > 0 && (
        <section id="trabajos" className="py-20 sm:py-28 bg-slate-900/50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6">
              <div>
                <span className="text-sm uppercase tracking-wider text-white/40 mb-2 block">Portfolio</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                  Trabajos <span style={{ color: primaryColor }}>realizados</span>
                </h2>
              </div>
            </div>

            {/* Masonry-like grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleGallery.map((item, idx) => (
                <div 
                  key={item.id}
                  className={`group relative rounded-3xl overflow-hidden cursor-pointer ${
                    idx === 0 ? 'md:col-span-2 md:row-span-2' : ''
                  }`}
                >
                  <img 
                    src={item.image_url} 
                    alt={item.title}
                    className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${
                      idx === 0 ? 'h-96 md:h-full' : 'h-64'
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h4 className="text-xl font-bold text-white mb-1">{item.title}</h4>
                      {item.description && (
                        <p className="text-white/70 text-sm">{item.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {gallery.length > 3 && !showAllGallery && (
              <div className="text-center mt-10">
                <Button 
                  variant="outline"
                  onClick={() => setShowAllGallery(true)}
                  className="rounded-full border-white/20 text-white hover:bg-white/10"
                >
                  Ver más trabajos
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ========== PROCESO ========== */}
      {work_process.length > 0 && (
        <section id="proceso" className="py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <span className="text-sm uppercase tracking-wider text-white/40 mb-2 block">Proceso</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                Cómo <span style={{ color: primaryColor }}>trabajamos</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {work_process.map((step, idx) => (
                <div key={idx} className="relative group">
                  {/* Connection line */}
                  {idx < work_process.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-1/2 w-full h-px bg-gradient-to-r from-white/20 to-transparent" />
                  )}
                  
                  <div className="bg-white/5 rounded-3xl p-8 border border-white/10 hover:border-white/20 transition-colors">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold mb-6"
                      style={{ backgroundColor: `${primaryColor}30`, color: primaryColor }}
                    >
                      {step.step}
                    </div>
                    <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                    <p className="text-white/60">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== TESTIMONIALS - Single Card Carousel ========== */}
      {testimonials.length > 0 && (
        <section id="opiniones" className="py-20 sm:py-28 bg-slate-900/50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-16">
              <span className="text-sm uppercase tracking-wider text-white/40 mb-2 block">Opiniones</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                Lo que dicen <span style={{ color: primaryColor }}>nuestros clientes</span>
              </h2>
            </div>

            {/* Single testimonial card */}
            <div className="relative bg-white/5 rounded-3xl p-8 md:p-12 border border-white/10">
              <Quote size={48} className="text-white/10 absolute top-8 left-8" />
              
              <div className="relative z-10">
                <p className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8">
                  "{testimonials[activeTestimonial]?.text}"
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-xl font-bold">
                      {testimonials[activeTestimonial]?.name?.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-semibold">{testimonials[activeTestimonial]?.name}</h4>
                      <p className="text-sm text-white/50">{testimonials[activeTestimonial]?.service}</p>
                    </div>
                  </div>
                  <div className="flex">
                    {[...Array(testimonials[activeTestimonial]?.rating || 5)].map((_, i) => (
                      <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Dots navigation */}
              {testimonials.length > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {testimonials.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveTestimonial(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === activeTestimonial 
                          ? 'w-8' 
                          : 'bg-white/30'
                      }`}
                      style={{ backgroundColor: idx === activeTestimonial ? primaryColor : undefined }}
                    />
                  ))}
                </div>
              )}

              {testimonials[activeTestimonial]?.is_demo && (
                <p className="text-xs text-white/30 text-center mt-4">Ejemplo demostrativo</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ========== ZONA DE SERVICIO ========== */}
      {service_areas.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <span className="text-sm uppercase tracking-wider text-white/40 mb-4 block">Zona de cobertura</span>
            <div className="flex flex-wrap justify-center gap-3">
              {service_areas.map((area, idx) => (
                <span 
                  key={idx}
                  className="px-4 py-2 bg-white/5 rounded-full text-sm text-white/70 border border-white/10"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== CTA FINAL ========== */}
      <section id="cta-section" className="py-20 sm:py-28 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{ 
            background: `radial-gradient(ellipse at center, ${primaryColor}, transparent 70%)`
          }}
        />
        
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            ¿Listo para <span style={{ color: primaryColor }}>empezar</span>?
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            Solicita tu presupuesto sin compromiso. Te respondemos en menos de 24 horas con una propuesta personalizada.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg"
              onClick={handlePresupuesto}
              className="rounded-full px-10 py-6 text-base font-semibold group"
              style={{ backgroundColor: primaryColor }}
            >
              Solicitar presupuesto
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => handleWhatsApp()}
              className="rounded-full px-10 py-6 text-base border-white/20 text-white hover:bg-white/10"
            >
              <MessageCircle size={18} className="mr-2" />
              Escribir ahora
            </Button>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="bg-slate-950 border-t border-white/10 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                {logo ? (
                  <img src={logo} alt={name} className="h-10 w-auto" />
                ) : (
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center font-bold"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {name?.charAt(0)}
                  </div>
                )}
                <span className="font-bold text-xl">{name}</span>
              </div>
              <p className="text-white/50 max-w-md">{description}</p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4 text-white/80">Contacto</h4>
              <div className="space-y-3 text-sm text-white/50">
                {phone && (
                  <a href={`tel:${phone}`} className="flex items-center gap-3 hover:text-white transition-colors">
                    <Phone size={16} />
                    {phone}
                  </a>
                )}
                {email && (
                  <a href={`mailto:${email}`} className="flex items-center gap-3 hover:text-white transition-colors">
                    <Mail size={16} />
                    {email}
                  </a>
                )}
                <div className="flex items-center gap-3">
                  <MapPin size={16} />
                  {city}{province && `, ${province}`}
                </div>
              </div>
            </div>

            {/* Hours */}
            {business_hours && (
              <div>
                <h4 className="font-semibold mb-4 text-white/80">Horario</h4>
                <div className="space-y-2 text-sm text-white/50">
                  {business_hours.monday && (
                    <div className="flex justify-between gap-4">
                      <span>Lun - Vie</span>
                      <span>{business_hours.monday.start} - {business_hours.monday.end}</span>
                    </div>
                  )}
                  {business_hours.saturday && (
                    <div className="flex justify-between gap-4">
                      <span>Sábado</span>
                      <span>{business_hours.saturday.start} - {business_hours.saturday.end}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-center text-sm text-white/30">
            <p>© {new Date().getFullYear()} {name}. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* ========== BARRA FIXA MOBILE ========== */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
        <div className="mx-4 mb-4">
          <div 
            className="rounded-2xl overflow-hidden backdrop-blur-xl border border-white/10"
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.95)' }}
          >
            <div className="grid grid-cols-3">
              <button 
                onClick={() => handleWhatsApp()}
                className="flex flex-col items-center justify-center py-4 text-green-400 active:bg-white/5 transition-colors"
              >
                <MessageCircle size={22} />
                <span className="text-xs mt-1 font-medium">WhatsApp</span>
              </button>
              <button 
                onClick={handlePresupuesto}
                className="flex flex-col items-center justify-center py-4 active:bg-white/5 transition-colors"
                style={{ color: primaryColor }}
              >
                <FileCheck size={22} />
                <span className="text-xs mt-1 font-medium">Presupuesto</span>
              </button>
              <button 
                onClick={handleCita}
                className="flex flex-col items-center justify-center py-4 text-white/70 active:bg-white/5 transition-colors"
              >
                <Calendar size={22} />
                <span className="text-xs mt-1 font-medium">Cita</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for mobile bottom bar */}
      <div className="h-24 lg:hidden" />
    </div>
  );
};

export default TemplateBarcelona;
